// Website visitors (RB2B + the website's forms → app/web_visitors.py; the site's own page-view
// snippet + ?aid= email clicks → app/web_beacon.py).
//   window.WebsiteTrackingCard   Settings → Integrations: the two webhook URLs + the Tag Manager snippet (admin)
//   window.WebsiteVisitorsPanel  Emails → Website tab: who visited; Add / Dismiss the ones Atlas doesn't know
//   window.WebsiteVisitsCard     contact + company pages: visits in the last 30 days and the latest pages

const WV_LABEL = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-4)' };

const wvWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) });
};

// https://industrypro.com/selling-a-business/?utm=x → /selling-a-business/
const wvPath = (url) => {
  if (!url) return '—';
  try { const u = new URL(url); return u.pathname || '/'; } catch (_) { return url; }
};

// ── Settings → Integrations ──────────────────────────────────────────────────
const WebsiteTrackingCard = () => {
  const [s, setS] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const reload = React.useCallback(() => {
    window.AtlasApi.webTrackingSettings().then(setS).catch((e) => setS({ error: e.message }));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); window.pushToast({ type: 'success', title: 'Copied' }); }
    catch (_) { window.pushToast({ type: 'error', title: 'Could not copy' }); }
  };
  const rotate = async (source, label) => {
    const ok = await window.showConfirm({ title: `New ${label} URL?`, body: 'The current URL stops working right away.', confirmLabel: 'Replace URL', danger: true });
    if (!ok) return;
    setBusy(source);
    try { setS(await window.AtlasApi.webTrackingRotate(source)); window.pushToast({ type: 'success', title: `${label} URL replaced` }); }
    catch (e) { window.pushToast({ type: 'error', title: 'Could not replace the URL', body: e.message }); }
    finally { setBusy(null); }
  };

  // RB2B history: a dry run first (what the file holds, what Atlas reads), then the import.
  const fileRef = React.useRef(null);
  // RB2B history: preview the file (instant, no database), confirm, then the
  // import runs in the background on the server and this card polls it.
  const [imp, setImp] = React.useState(null);           // the server's import state
  const pollRef = React.useRef(null);
  const poll = React.useCallback(() => {
    clearTimeout(pollRef.current);
    window.AtlasApi.importRb2bState().then((st) => {
      setImp(st);
      if (st.status === 'running') { pollRef.current = setTimeout(poll, 3000); return; }
      setBusy((b) => (b === 'import' ? null : b));
      if (st.status === 'done' && st.counts) {
        const c = st.counts;
        window.pushToast({ type: 'success', title: `Imported ${c.imported.toLocaleString()} RB2B profiles`,
          body: `${c.known.toLocaleString()} matched to Atlas · ${c.not_in_atlas.toLocaleString()} in Emails → Website → Not in Atlas` });
      } else if (st.status === 'error') {
        window.pushToast({ type: 'error', title: 'RB2B import failed', body: st.error || '' });
      }
    }).catch(() => setBusy((b) => (b === 'import' ? null : b)));
  }, []);
  React.useEffect(() => {
    // A run started earlier (or in another tab) keeps showing its progress.
    window.AtlasApi.importRb2bState().then((st) => {
      setImp(st);
      if (st.status === 'running') { setBusy('import'); poll(); }
    }).catch(() => {});
    return () => clearTimeout(pollRef.current);
  }, [poll]);

  const importCsv = async (file) => {
    if (!file) return;
    setBusy('import');
    let started = false;
    try {
      const d = await window.AtlasApi.importRb2bCsv(file, true);
      const lines = [
        `${d.people.toLocaleString()} people · ${d.companies.toLocaleString()} companies · ${d.page_views.toLocaleString()} page views`,
        d.no_date || d.no_identity ? `Skipped: ${d.no_date} without a date, ${d.no_identity} without a person or company` : null,
        `Columns read: ${d.columns.used.join(', ') || 'none'}`,
        d.columns.ignored.length ? `Ignored: ${d.columns.ignored.join(', ')}` : null,
      ].filter(Boolean);
      if (!d.importable) { window.pushToast({ type: 'error', title: 'Nothing to import', body: lines.join('\n') }); return; }
      const ok = await window.showConfirm({ title: `Import ${d.importable.toLocaleString()} RB2B profiles?`,
        body: <div style={{ display: 'grid', gap: 4 }}>{lines.map((l) => <div key={l}>{l}</div>)}</div>, confirmLabel: 'Import' });
      if (!ok) return;
      await window.AtlasApi.importRb2bCsv(file, false);
      started = true;
      pollRef.current = null;
      poll();
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Import failed', body: e.message });
    } finally {
      if (!started) setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  const importLabel = busy === 'import'
    ? (imp && imp.status === 'running' && imp.total ? `Importing… ${(imp.done || 0).toLocaleString()} / ${imp.total.toLocaleString()}` : 'Importing…')
    : 'Import RB2B CSV';

  if (s === null) return null;
  const rows = [
    { source: 'rb2b', label: 'RB2B webhook' },
    { source: 'form', label: 'Website form webhook' },
  ];
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>Website visitors</div>
        <div style={{ flex: 1 }} />
        {!s.error && (
          <window.Btn size="sm" variant="outline" disabled={busy !== null} onClick={() => fileRef.current && fileRef.current.click()}>
            {importLabel}
          </window.Btn>
        )}
        <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={(e) => importCsv(e.target.files && e.target.files[0])} />
      </div>
      {imp && imp.status === 'done' && imp.counts && (
        <div style={{ marginTop: 6 }}>
          <window.Pill hue="slate" sz="sm">
            RB2B history imported {wvWhen(imp.updated_at)} · {imp.counts.imported.toLocaleString()} profiles · {imp.counts.known.toLocaleString()} matched
          </window.Pill>
        </div>
      )}
      {imp && imp.status === 'stale' && (
        <div style={{ marginTop: 6 }}><window.Pill hue="amber" sz="sm">The last RB2B import stopped part-way — import the file again to finish it</window.Pill></div>
      )}
      {s.error && <div style={{ marginTop: 8 }}><window.Pill hue="rose" sz="sm">{s.error}</window.Pill></div>}
      {!s.error && rows.map(({ source, label }) => {
        const r = s[source] || {};
        const lastOk = r.last_ok && r.last_ok.at;
        const lastErr = r.last_error && r.last_error.at;
        const errNewer = lastErr && (!lastOk || lastErr > lastOk);
        return (
          <div key={source} data-web-hook={source} style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={WV_LABEL}>{label}</span>
              {errNewer ? <window.Pill hue="rose" sz="sm" title={r.last_error.detail || ''}>Error {wvWhen(lastErr)}</window.Pill>
                : lastOk ? <window.Pill hue="green" dot sz="sm">Last received {wvWhen(lastOk)}</window.Pill>
                : <window.Pill hue="slate" sz="sm">Nothing received yet</window.Pill>}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
              <input readOnly value={r.url || ''} onFocus={(e) => e.target.select()}
                style={{ flex: 1, minWidth: 0, padding: '6px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 5,
                         fontFamily: 'var(--font-mono)', background: 'var(--surface-2, var(--surface))', color: 'var(--text)' }} />
              <window.Btn size="sm" variant="outline" onClick={() => copy(r.url)}>Copy</window.Btn>
              <window.Btn size="sm" variant="ghost" disabled={busy !== null} onClick={() => rotate(source, label)}>{busy === source ? 'Replacing…' : 'New URL'}</window.Btn>
            </div>
          </div>
        );
      })}
      {!s.error && s.site && (
        <div data-web-hook="site" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={WV_LABEL}>Tag Manager snippet</span>
            {s.site.last_view_at ? <window.Pill hue="green" dot sz="sm">Last page view {wvWhen(s.site.last_view_at)}</window.Pill>
              : <window.Pill hue="slate" sz="sm">Nothing received yet</window.Pill>}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 6 }}>
            <textarea readOnly value={s.site.snippet || ''} rows={5} onFocus={(e) => e.target.select()}
              style={{ flex: 1, minWidth: 0, padding: '6px 8px', fontSize: 11.5, border: '1px solid var(--border)', borderRadius: 5, resize: 'vertical',
                       fontFamily: 'var(--font-mono)', background: 'var(--surface-2, var(--surface))', color: 'var(--text)' }} />
            <window.Btn size="sm" variant="outline" onClick={() => copy(s.site.snippet)}>Copy</window.Btn>
          </div>
        </div>
      )}
      {!s.error && <WebVisitAlertsRow />}
    </div>
  );
};

// Warm visit alerts (app/web_visit_alerts.py): the on/off switch and who is
// DMed besides the record owner. Saved on change.
const WebVisitAlertsRow = () => {
  const [a, setA] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { window.AtlasApi.webVisitAlerts().then(setA).catch(() => setA(null)); }, []);
  const save = async (patch) => {
    setSaving(true);
    try { setA(await window.AtlasApi.webVisitAlertsSave(patch)); }
    catch (e) { window.pushToast({ type: 'error', title: 'Could not save alerts', body: e.message }); }
    finally { setSaving(false); }
  };
  if (!a) return null;
  const on = !!a.enabled;
  const options = (a.members || []).map((m) => ({ value: m.email, label: m.name, hint: m.email }));
  return (
    <div data-web-hook="alerts" style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={WV_LABEL}>Warm visit alerts</span>
        <button type="button" role="switch" aria-checked={on} disabled={saving} onClick={() => save({ enabled: !on })}
          title="When someone from a company with an open deal, in a campaign, or on a call list visits the site, DM the owner and the people below"
          style={{ width: 32, height: 18, borderRadius: 999, padding: 1.5, flexShrink: 0, border: 'none', cursor: 'pointer',
                   background: on ? 'oklch(0.55 0.12 155)' : 'oklch(0.85 0.005 270)', transition: 'background .15s' }}>
          <span style={{ display: 'block', width: 15, height: 15, borderRadius: 999, background: 'var(--surface)',
                         transform: on ? 'translateX(14px)' : 'translateX(0)', transition: 'transform .15s',
                         boxShadow: '0 1px 2px rgba(0,0,0,.15)' }} />
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Owner and</span>
        <window.Picker multiple size="sm" minWidth={220} disabled={saving || !on} placeholder="Nobody else"
          value={a.recipients || []} options={options} onChange={(v) => save({ recipients: v })} />
      </div>
    </div>
  );
};

// ── Emails → Website tab ─────────────────────────────────────────────────────
const WV_STATUSES = [
  { key: 'new', label: 'Not in Atlas' },
  { key: 'email', label: 'From emails' },
  { key: 'matched', label: 'Known' },
  { key: 'added', label: 'Added' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'all', label: 'All' },
];

// RB2B history from a CSV import: page views since first seen, and the
// most-viewed recent pages (RB2B gives no time per page, so no dates here).
const RB2BHistory = ({ h, compact = false }) => {
  if (!h) return null;
  const since = h.first_seen_at ? new Date(h.first_seen_at).toLocaleDateString([], { month: 'short', year: 'numeric' }) : null;
  return (
    <div data-rb2b-history style={{ display: 'grid', gap: 2 }}>
      <div style={{ fontSize: compact ? 11.5 : 12, color: 'var(--text-2)' }}>
        {h.page_views.toLocaleString()} page view{h.page_views === 1 ? '' : 's'}{since ? ` since ${since}` : ''} · RB2B
      </div>
      {(h.top_pages || []).slice(0, compact ? 5 : 8).map((p) => (
        <div key={p.page_url} style={{ display: 'flex', gap: 8, fontSize: 11.5, minWidth: 0 }}>
          <a href={p.page_url || undefined} target="_blank" rel="noopener noreferrer"
            style={{ color: 'var(--accent-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
            {wvPath(p.page_url)}
          </a>
          {p.views > 1 && <span style={{ color: 'var(--text-4)' }} className="num">×{p.views}</span>}
        </div>
      ))}
    </div>
  );
};

const VisitorVisits = ({ id }) => {
  const [rows, setRows] = React.useState(null);
  const [hist, setHist] = React.useState(null);
  React.useEffect(() => {
    window.AtlasApi.webVisitorVisits(id).then((r) => { setRows(r.visits || []); setHist(r.history || null); }).catch(() => setRows([]));
  }, [id]);
  if (rows === null) return <div style={{ padding: '6px 0', fontSize: 12, color: 'var(--text-4)' }}>Loading…</div>;
  return (
    <div style={{ display: 'grid', gap: 2, padding: '4px 0 8px' }}>
      {hist && <div style={{ marginBottom: rows.length ? 6 : 0, maxWidth: 560 }}><RB2BHistory h={hist} /></div>}
      {rows.map((v) => (
        <div key={v.id} style={{ display: 'flex', gap: 10, fontSize: 12, color: 'var(--text-2)' }}>
          <span style={{ width: 90, color: 'var(--text-4)', flexShrink: 0 }}>{wvWhen(v.seen_at)}</span>
          <a href={v.page_url || undefined} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {v.kind === 'form' ? `Form · ${wvPath(v.page_url)}` : wvPath(v.page_url)}
          </a>
          {(v.tags || []).includes('email') && <window.Pill hue="violet" sz="sm">Email click</window.Pill>}
          {v.referrer && <span style={{ color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>from {v.referrer}</span>}
        </div>
      ))}
    </div>
  );
};

// The site's own numbers (the Tag Manager snippet), last 30 days.
const SiteStats = () => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => { window.AtlasApi.webSiteStats({ days: 30 }).then(setD).catch(() => setD(null)); }, []);
  if (!d || !d.views) return null;
  const tiles = [
    ['Page views', d.views], ['Visitors', d.visitors], ['Email clicks', d.email_clicks], ['Known visitors', d.known_visitors],
  ];
  return (
    <div data-site-stats style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline' }}>
      <span style={WV_LABEL}>Last {d.days} days</span>
      {tiles.map(([label, n]) => (
        <div key={label} style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
          <span style={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n.toLocaleString()}</span>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{label}</span>
        </div>
      ))}
    </div>
  );
};

const WebsiteVisitorsPanel = ({ chip, setContactId, setCompanyId, canWrite = true }) => {
  const [status, setStatus] = React.useState('new');
  const [sort, setSort] = React.useState('recent');
  const [q, setQ] = React.useState('');
  const [qLive, setQLive] = React.useState('');
  const [limit, setLimit] = React.useState(50);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [open, setOpen] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const seq = React.useRef(0);

  React.useEffect(() => { const t = setTimeout(() => setQ(qLive.trim()), 250); return () => clearTimeout(t); }, [qLive]);
  const reload = React.useCallback(() => {
    const mine = ++seq.current;
    const params = { status, limit, sort };
    if (q) params.q = q;
    window.AtlasApi.webVisitors(params)
      .then((r) => { if (mine === seq.current) { setData(r); setErr(null); } })
      .catch((e) => { if (mine === seq.current) setErr(e.message); });
  }, [status, q, limit, sort]);
  React.useEffect(() => { reload(); }, [reload]);

  const act = async (v, kind) => {
    setBusy(v.id);
    try {
      if (kind === 'add') {
        const r = await window.AtlasApi.webVisitorAdd(v.id);
        const made = [r.created_contact && 'contact', r.created_company && 'company'].filter(Boolean);
        window.pushToast({ type: 'success', title: made.length ? `Added ${made.join(' + ')}` : 'Linked to the existing record' });
      } else if (kind === 'dismiss') {
        await window.AtlasApi.webVisitorDismiss(v.id);
      } else {
        await window.AtlasApi.webVisitorRestore(v.id);
      }
      reload();
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Could not update the visitor', body: e.message });
    } finally { setBusy(null); }
  };

  const counts = (data && data.counts) || {};
  const countOf = (k) => (k === 'all' ? WV_STATUSES.filter((s) => s.key !== 'all' && s.key !== 'email').reduce((a, s) => a + (counts[s.key] || 0), 0) : (counts[k] || 0));
  const items = (data && data.items) || [];
  const cell = { padding: '8px 10px', fontSize: 12.5, borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
  const head = { ...cell, ...WV_LABEL, fontWeight: 500, textAlign: 'left', position: 'sticky', top: 0, background: 'var(--surface)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SiteStats />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {WV_STATUSES.map((s) => chip(status === s.key, `${s.label}${data ? ` · ${countOf(s.key).toLocaleString()}` : ''}`, () => { setStatus(s.key); setOpen(null); }, `wv-${s.key}`))}
        </div>
        <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
        <div style={{ display: 'flex', gap: 4 }}>
          {chip(sort === 'recent', 'Latest', () => setSort('recent'), 'wv-sort-recent')}
          {chip(sort === 'visits', 'Most visits', () => setSort('visits'), 'wv-sort-visits')}
        </div>
        <input value={qLive} onChange={(e) => setQLive(e.target.value)} placeholder="Search name, company, email"
          style={{ marginLeft: 'auto', width: 240, padding: '5px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)' }} />
      </div>
      {err && <div style={{ color: 'var(--rose-fg, #b42318)', fontSize: 12 }}>Couldn’t load visitors: {err}</div>}
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--surface)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={head}>Person</th>
              <th style={head}>Company</th>
              <th style={head}>Location</th>
              <th style={{ ...head, textAlign: 'right' }}>Visits</th>
              <th style={head}>Last seen</th>
              <th style={{ ...head, width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {data === null && !err && <tr><td colSpan={6} style={{ ...cell, color: 'var(--text-4)' }}>Loading…</td></tr>}
            {data && items.length === 0 && <tr><td colSpan={6} style={{ ...cell, color: 'var(--text-4)' }}>No visitors</td></tr>}
            {items.map((v) => (
              <React.Fragment key={v.id}>
                <tr data-visitor={v.id} style={{ cursor: 'pointer' }} onClick={() => setOpen(open === v.id ? null : v.id)}>
                  <td style={cell}>
                    {v.contact_id ? (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setContactId && setContactId(v.contact_id); }}
                        style={{ border: 0, background: 'none', padding: 0, color: 'var(--accent-text)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>
                        {v.atlas_contact_name || v.name || v.email || 'Contact'}
                      </button>
                    ) : <span style={{ fontWeight: 600 }}>{v.name || (v.kind === 'company' ? '—' : v.email || '—')}</span>}
                    {v.title && <div style={{ color: 'var(--text-3)', fontSize: 11.5 }}>{v.title}</div>}
                    {v.email_click && status !== 'email' && <div><window.Pill hue="violet" sz="sm">Email click</window.Pill></div>}
                    {(v.rb2b_tags || []).some((t) => /hot lead/i.test(t)) && <div><window.Pill hue="rose" sz="sm">Hot lead</window.Pill></div>}
                    {v.linkedin_url && (
                      <a href={v.linkedin_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: 11, color: 'var(--text-4)' }}>LinkedIn</a>
                    )}
                  </td>
                  <td style={cell}>
                    {v.company_id ? (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setCompanyId && setCompanyId(v.company_id); }}
                        style={{ border: 0, background: 'none', padding: 0, color: 'var(--accent-text)', cursor: 'pointer', fontSize: 12.5 }}>
                        {v.atlas_company_name || v.company_name || v.company_domain}
                      </button>
                    ) : <span>{v.company_name || v.company_domain || '—'}</span>}
                    <div style={{ color: 'var(--text-4)', fontSize: 11 }}>{[v.industry, v.employee_count, v.revenue_estimate].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td style={{ ...cell, color: 'var(--text-3)' }}>{v.location || '—'}</td>
                  <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{v.visit_count}</td>
                  <td style={cell}>
                    <div>{wvWhen(v.last_seen_at)}</div>
                    <div style={{ color: 'var(--text-4)', fontSize: 11, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wvPath(v.last_page)}</div>
                  </td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    {canWrite && v.status === 'new' && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <window.Btn size="xs" variant="accent" disabled={busy === v.id} onClick={() => act(v, 'add')}>Add</window.Btn>
                        <window.Btn size="xs" variant="ghost" disabled={busy === v.id} onClick={() => act(v, 'dismiss')}>Dismiss</window.Btn>
                      </div>
                    )}
                    {canWrite && v.status === 'dismissed' && (
                      <window.Btn size="xs" variant="ghost" disabled={busy === v.id} onClick={() => act(v, 'restore')}>Restore</window.Btn>
                    )}
                    {v.status !== 'new' && v.status !== 'dismissed' && status === 'all' && (
                      <window.Pill hue={v.status === 'added' ? 'green' : 'teal'} sz="sm">{v.status === 'added' ? 'Added' : 'Known'}</window.Pill>
                    )}
                  </td>
                </tr>
                {open === v.id && (
                  <tr><td colSpan={6} style={{ ...cell, background: 'var(--surface-2, var(--surface))' }}><VisitorVisits id={v.id} /></td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {data && data.total > items.length && (
        <div><window.Btn size="sm" variant="outline" onClick={() => setLimit((n) => n + 50)}>Show more ({(data.total - items.length).toLocaleString()})</window.Btn></div>
      )}
    </div>
  );
};

// ── record pages ─────────────────────────────────────────────────────────────
// variant 'rail' = the v2 right column (SummaryCard); 'card' = the classic pages' Section.
const WebsiteVisitsCard = ({ contactId, companyId, variant = 'rail' }) => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    const params = contactId ? { contact_id: contactId } : { company_id: companyId };
    window.AtlasApi.recordWebVisits(params).then((r) => { if (alive) setD(r); }).catch(() => { if (alive) setD(null); });
    return () => { alive = false; };
  }, [contactId, companyId]);
  if (!d || !d.total) return null;
  const preview = `${d.total.toLocaleString()} total · last ${wvWhen(d.last_seen_at)}`;
  const Card = variant === 'card' ? window.Section : window.SummaryCard;
  return (
    <Card title="Website" collapsible id={contactId ? 'contact.website' : 'company.website'} defaultOpen={d.recent > 0 || !!d.history} preview={preview}
      style={variant === 'card' ? { marginBottom: 14 } : undefined}>
      <div style={{ padding: '2px 14px 10px', display: 'grid', gap: 3 }}>
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 2 }}>
          {d.recent.toLocaleString()} visit{d.recent === 1 ? '' : 's'} in the last {d.days} days · {d.total.toLocaleString()} total
        </div>
        {d.history && <div style={{ margin: '2px 0 4px' }}><RB2BHistory h={d.history} compact /></div>}
        {d.visits.slice(0, 8).map((v) => (
          <div key={v.id} style={{ display: 'flex', gap: 8, fontSize: 11.5, minWidth: 0 }}>
            <span style={{ width: 54, color: 'var(--text-4)', flexShrink: 0 }}>{wvWhen(v.seen_at)}</span>
            <a href={v.page_url || undefined} target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--accent-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
              {v.kind === 'form' ? `Form · ${wvPath(v.page_url)}` : wvPath(v.page_url)}
            </a>
            {(v.tags || []).includes('email') && <window.Pill hue="violet" sz="sm">Email click</window.Pill>}
            {companyId && v.who && <span style={{ color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{v.who}</span>}
          </div>
        ))}
      </div>
    </Card>
  );
};

window.WebsiteTrackingCard = WebsiteTrackingCard;
window.WebsiteVisitorsPanel = WebsiteVisitorsPanel;
window.WebsiteVisitsCard = WebsiteVisitsCard;
